'use client'

import Image from 'next/image'
import { Plus, Clock } from 'lucide-react'
import type { MenuItemView } from '@/lib/microsite/menuItemView'
import { useLowStock } from '@/components/menu/LowStockContext'
import { LowStockBadge } from '@/components/menu/LowStockBadge'

/**
 * Presentación por defecto de la carta: tarjetas con foto grande en rejilla.
 * Es la que la página tuvo siempre — se extrajo tal cual de MenuSection para
 * que puedan convivir otras presentaciones sin duplicar la lógica de dinero.
 *
 * 🔴 Recibe `MenuItemView` y NO el ítem crudo: el precio ya viene resuelto
 * (`effectiveItemCardPrice`) y acá no hay forma de pintar otro.
 */
export function MenuLayoutClassic({
  views,
  onTap,
  addLabel,
  soldOutLabel,
  emptyLabel,
  fromLabel,
}: {
  views: MenuItemView[]
  onTap: (id: string) => void
  addLabel: string
  /** Rótulo del 86 ("Agotado"): el plato se muestra apagado, no se oculta. */
  soldOutLabel: string
  emptyLabel: string
  /** «desde» de un producto con variantes de distinto precio (súper online, R6). */
  fromLabel?: string
}) {
  const lowStock = useLowStock()
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mt-3 sm:mt-6">
      {views.map((v) => {
        // Agotado (86) manda sobre la ventana horaria: mismo apagado visual,
        // pero con badge "Agotado" en vez del reloj.
        const blocked = v.soldOut || !v.availableNow
        // «¡Quedan pocas!» solo sobre lo que todavía se puede pedir.
        const few = !blocked && lowStock.item(v.id)
        return (
        <button
          key={v.id}
          type="button"
          onClick={() => onTap(v.id)}
          disabled={blocked}
          className={`group text-left overflow-hidden transition ${!blocked ? 'cursor-pointer hover:-translate-y-0.5 active:scale-[0.99]' : 'cursor-not-allowed'}`}
          style={{
            background: 'var(--ms-surface)',
            borderRadius: 'var(--ms-radius)',
            boxShadow: 'var(--ms-card-shadow)',
            border: '1px solid var(--ms-border)',
            opacity: !blocked ? 1 : 0.62,
          }}
        >
          {v.imageUrl && (
            <div className="relative w-full" style={{ aspectRatio: '16 / 11' }}>
              <Image
                src={v.imageUrl}
                alt={v.name}
                fill
                sizes="(max-width: 640px) 100vw, 33vw"
                className="object-cover"
                style={{ objectPosition: v.focal, filter: !blocked ? undefined : 'grayscale(1)' }}
              />
              {v.badge && (
                <span className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold text-white shadow-sm" style={{ background: v.badge.color }}>
                  <span>{v.badge.icon}</span>{v.badge.label}
                </span>
              )}
              {v.soldOut ? (
                <span
                  className="absolute top-3 right-3 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide text-white shadow-sm"
                  style={{ background: 'rgba(17,17,17,0.82)' }}
                >
                  {soldOutLabel}
                </span>
              ) : v.schedLabel && (
                <span
                  className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold text-white shadow-sm"
                  style={{ background: v.availableNow ? 'rgba(17,17,17,0.82)' : 'var(--ms-accent)' }}
                >
                  <Clock className="w-3 h-3" strokeWidth={2.5} />{v.schedLabel}
                </span>
              )}
              {few && (
                // Abajo a la izquierda: arriba ya están el badge del dueño y el
                // horario/agotado, y sobre la foto es donde más se ve.
                <span className="absolute bottom-3 left-3">
                  <LowStockBadge size="lg" />
                </span>
              )}
            </div>
          )}
          <div className="p-3.5 sm:p-5">
            {!v.imageUrl && (v.badge || v.schedLabel || v.soldOut || few) && (
              <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                {v.badge && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ background: v.badge.color }}>
                    <span>{v.badge.icon}</span>{v.badge.label}
                  </span>
                )}
                {v.soldOut ? (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide text-white"
                    style={{ background: 'rgba(17,17,17,0.82)' }}
                  >
                    {soldOutLabel}
                  </span>
                ) : v.schedLabel && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
                    style={{ background: v.availableNow ? 'rgba(17,17,17,0.82)' : 'var(--ms-accent)' }}
                  >
                    <Clock className="w-3 h-3" strokeWidth={2.5} />{v.schedLabel}
                  </span>
                )}
                {few && <LowStockBadge size="md" />}
              </div>
            )}
            <h3 className="text-[16px] sm:text-[18px] font-bold leading-tight" style={{ color: 'var(--ms-text)', fontFamily: 'var(--ms-font-display)' }}>
              {v.name}
            </h3>
            {v.description && (
              <p className="text-[13px] mt-1.5 leading-relaxed line-clamp-2" style={{ color: 'var(--ms-text-muted)' }}>
                {v.description}
              </p>
            )}
            <div className="flex items-center justify-between mt-3 pt-2.5" style={{ borderTop: '1px solid var(--ms-border)' }}>
              <span className="text-[16px] sm:text-[18px] font-bold" style={{ color: 'var(--ms-text)', fontFamily: 'var(--ms-font-display)' }}>
                {v.priceUnit || v.priceFrom ? (
                  // Súper online (R6): «$3.99/lb» o «desde $2.50». Un plato no trae
                  // ninguna de las dos claves y pinta la rama de abajo, la de main.
                  `${v.priceFrom && fromLabel ? `${fromLabel} ` : ''}$${v.cardPrice.toFixed(2)}${v.priceUnit ? `/${v.priceUnit}` : ''}`
                ) : (
                  <>${v.cardPrice.toFixed(2)}</>
                )}
              </span>
              {v.soldOut ? (
                <span className="inline-flex items-center text-[12px] font-black uppercase tracking-wide" style={{ color: 'var(--ms-text-muted)' }}>
                  {soldOutLabel}
                </span>
              ) : v.availableNow ? (
                <span className="inline-flex items-center gap-1 text-[13px] font-bold" style={{ color: 'var(--ms-accent)' }}>
                  {addLabel} <Plus className="w-3.5 h-3.5" />
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: 'var(--ms-text-muted)' }}>
                  <Clock className="w-3.5 h-3.5" />{v.schedLabel}
                </span>
              )}
            </div>
          </div>
        </button>
        )
      })}

      {views.length === 0 && (
        <p className="col-span-full text-sm text-center py-8" style={{ color: 'var(--ms-text-muted)' }}>
          {emptyLabel}
        </p>
      )}
    </div>
  )
}
